import 'react-datepicker/dist/react-datepicker.css';
import React from 'react';
import DatePicker from 'react-datepicker';
import {Control, Controller, FieldErrors, FieldValues, Path} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {type TypedTranslation} from '../../../i18n';

interface IReactHookFormDateTimeFieldProps<T extends FieldValues> {
    text: string;
    fieldName: Path<T>;
    control: Control<T>;
    errors: FieldErrors<T>;
    textClassName?: string;
    disablePast?: boolean;
    minDateTime?: string;
    disabled?: boolean;
    dataCy?: string;
}

const toDate = (isoUtc?: string): Date | null => {
    if (!isoUtc) return null;
    const date = new Date(isoUtc);
    return Number.isNaN(date.getTime()) ? null : date;
};

interface IDateTimeInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    dataCy?: string;
}

const DateTimeInput = React.forwardRef<HTMLInputElement, IDateTimeInputProps>(({dataCy, ...props}, ref) => (
    <input {...props} ref={ref} data-cy={dataCy} className="c-datetime-field__input" />
));
DateTimeInput.displayName = 'DateTimeInput';

function ReactHookFormDateTimeField<T extends FieldValues>({
    text,
    fieldName,
    control,
    errors,
    textClassName,
    disablePast,
    minDateTime,
    disabled,
    dataCy,
}: IReactHookFormDateTimeFieldProps<T>): JSX.Element {
    const {t, i18n} = useTranslation();

    const isHr = i18n.language?.toLowerCase().startsWith('hr');
    const localeId = isHr ? 'hr' : 'en';
    // Croatian uses 24h time, English (en-US) uses 12h AM/PM.
    const timeFormat = isHr ? 'HH:mm' : 'h:mm aa';
    const dateFormat = isHr ? "dd.MM.yyyy. 'u' HH:mm" : 'MMM d, yyyy h:mm aa';

    const minBounds: number[] = [];
    if (disablePast) minBounds.push(Date.now());
    const minDateTimeMs = minDateTime ? new Date(minDateTime).getTime() : NaN;
    if (!Number.isNaN(minDateTimeMs)) minBounds.push(minDateTimeMs);
    const minMs = minBounds.length ? Math.max(...minBounds) : undefined;

    return (
        <div className="u-flex u-flex-col" style={{width: '100%'}}>
            <div className={`${textClassName || 'is-text'}`}>{text}</div>
            <Controller
                name={fieldName}
                control={control}
                render={({field}) => (
                    <div className="c-datetime-field">
                        <DatePicker
                            selected={toDate(field.value)}
                            onChange={(date: Date | null) => field.onChange(date ? date.toISOString() : '')}
                            onBlur={field.onBlur}
                            showTimeSelect
                            timeIntervals={15}
                            timeFormat={timeFormat}
                            dateFormat={dateFormat}
                            locale={localeId}
                            disabled={disabled}
                            minDate={minMs !== undefined ? new Date(minMs) : undefined}
                            filterTime={minMs !== undefined ? (time: Date) => time.getTime() >= minMs : undefined}
                            placeholderText={`${t('roomsPage.modals.selectDateTime' as TypedTranslation)}`}
                            popperPlacement="bottom-start"
                            portalId="portalContainerMain"
                            wrapperClassName="c-datetime-field__wrapper"
                            isClearable={!disabled}
                            customInput={<DateTimeInput dataCy={dataCy} />}
                        />
                    </div>
                )}
            />
            <div className="error-msg u-flex u-h-right">
                {errors[fieldName] && <span>{t(errors[fieldName]?.message as TypedTranslation)}</span>}
            </div>
        </div>
    );
}

export default ReactHookFormDateTimeField;
